import { 
  Home, 
  Users, 
  Building2, 
  Kanban, 
  Calendar, 
  Briefcase, 
  Building, 
  Scale, 
  Gavel, 
  FileText,
  Shield,
  ClipboardList,
  Settings,
  FileSignature,
  Layers,
  Activity,
  LucideIcon
} from "lucide-react";

/**
 * Navigation visibility levels for RBAC
 * - utility: Always visible to ALL authenticated users (Help, Sign Out)
 * - super_admin: Only visible to Super Admins (platform management)
 * - firm_owner: Visible to Firm Owners only (firm management)
 * - business_admin: Visible to Firm Owners and Business Admins
 * - staff: Visible to all business members (admin, attorney, paralegal, support)
 * 
 * NOTE: Super Admins ONLY see super_admin + utility items.
 * They do NOT see firm_owner, business_admin, or staff items.
 */
export type NavVisibility = 'utility' | 'super_admin' | 'firm_owner' | 'business_admin' | 'staff';

export interface NavItem {
  title: string;
  url: string;
  icon: LucideIcon;
  /** Who can see this item */
  visibility: NavVisibility;
  /** True = route not yet implemented, show "Soon" badge */
  comingSoon?: boolean;
  /** Match route end for active state */
  end?: boolean;
}

/**
 * Platform section items - Super Admin only
 * These are for cross-firm platform management
 */
export const platformItems: NavItem[] = [
  { 
    title: 'Overview', 
    url: '/platform', 
    icon: Shield, 
    visibility: 'super_admin',
    end: true
  },
  { 
    title: 'Firms', 
    url: '/platform/firms', 
    icon: Building, 
    visibility: 'super_admin'
  },
  { 
    title: 'All Businesses', 
    url: '/platform/businesses', 
    icon: Building2, 
    visibility: 'super_admin'
  },
  { 
    title: 'Platform Admins', 
    url: '/platform/admins', 
    icon: Shield, 
    visibility: 'super_admin'
  },
  { 
    title: 'Configuration', 
    url: '/platform/configuration', 
    icon: Settings, 
    visibility: 'super_admin'
  },
  { 
    title: 'Platform Audit', 
    url: '/platform/audit', 
    icon: ClipboardList, 
    visibility: 'super_admin', 
    comingSoon: true 
  },
  { 
    title: 'Observability', 
    url: '/platform/queue', 
    icon: Activity, 
    visibility: 'super_admin'
  },
];

/**
 * Observability items - visible to all staff (read-only for non-admin roles)
 * FAS-113: Moved to separate array for proper visibility across all business roles
 */
export const observabilityItems: NavItem[] = [
  { 
    title: 'Observability', 
    url: '/platform/queue', 
    icon: Activity, 
    visibility: 'staff'
  },
];

/**
 * Main navigation items - available to all authenticated users
 */
export const navigationItems: NavItem[] = [
  { 
    title: 'Dashboard', 
    url: '/dashboard', 
    icon: Home, 
    visibility: 'staff',
    end: true 
  },
  { 
    title: 'Assistants', 
    url: '/assistants', 
    icon: Users, 
    visibility: 'staff' 
  },
  { 
    title: 'Conciliation', 
    url: '/conciliation/dashboard', 
    icon: Scale, 
    visibility: 'staff' 
  },
  { 
    title: 'Calendar', 
    url: '/calendar', 
    icon: Calendar, 
    visibility: 'staff',
    comingSoon: true 
  },
  { 
    title: 'Pipeline', 
    url: '/pipeline', 
    icon: Kanban, 
    visibility: 'staff',
    comingSoon: true 
  },
  { 
    title: 'OCR Tool', 
    url: '/ocr-tool', 
    icon: FileText, 
    visibility: 'staff',
    comingSoon: true 
  },
];

/**
 * Management section items - visibility varies by role
 */
export const managementItems: NavItem[] = [
  {
    title: 'Organization',
    url: '/organization',
    icon: Building,
    visibility: 'staff'
  },
  { 
    title: 'Courts', 
    url: '/courts', 
    icon: Gavel, 
    visibility: 'staff' 
  },
  { 
    title: 'Suit Categories', 
    url: '/suit-categories', 
    icon: Gavel, 
    visibility: 'staff',
    comingSoon: true 
  },
  { 
    title: 'Templates', 
    url: '/templates', 
    icon: Briefcase, 
    visibility: 'staff' 
  },
  { 
    title: 'Letterheads', 
    url: '/letterhead-components', 
    icon: FileText, 
    visibility: 'staff' 
  },
  { 
    title: 'Signatures', 
    url: '/settings/signatures', 
    icon: FileSignature, 
    visibility: 'business_admin' 
  },
  { 
    title: 'Workspaces', 
    url: '/workspaces', 
    icon: Layers, 
    visibility: 'business_admin' 
  },
];
